
import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import PageTransition from '@/components/utils/PageTransition';
import { Server, ArrowLeft, ArrowUpRight, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AIInfrastructurePage = () => {
  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow pt-32 pb-20">
          <div className="container mx-auto px-6">
            <div className="mb-6">
              <Link to="/services" className="text-muted-foreground hover:text-primary flex items-center transition-colors">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to all services
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-12 mb-16">
              <div className="md:col-span-2">
                <div className="animate-fade-in">
                  <div className="bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400 p-3 rounded-xl inline-block mb-4">
                    <Server className="h-10 w-10" />
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
                    AI <span className="text-primary">Infrastructure</span>
                  </h1>
                  <p className="text-xl text-muted-foreground mb-6">
                    Robust hardware and software frameworks designed specifically for AI workloads.
                  </p>
                </div>

                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <p>
                    AI infrastructure provides the computing power, storage, and networking resources needed to train and deploy advanced AI models. Our AI infrastructure solutions help businesses build scalable, high-performance environments optimized for AI workloads.
                  </p>
                  
                  <h2>Our Approach</h2>
                  <p>
                    We design and implement AI infrastructure solutions tailored to your specific needs. Whether you're building a new AI platform or optimizing existing infrastructure, we ensure your environment is optimized for performance, efficiency, and scalability.
                  </p>
                  
                  <h2>Key Benefits</h2>
                  <ul>
                    <li>Accelerate AI model training and inference</li>
                    <li>Optimize resource utilization and reduce costs</li>
                    <li>Scale AI workloads efficiently</li>
                    <li>Improve collaboration among data scientists and engineers</li>
                    <li>Enhance model reproducibility and governance</li>
                    <li>Reduce time-to-market for AI applications</li>
                  </ul>
                </div>
              </div>

              <div className="md:col-span-1">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 shadow-subtle border border-gray-100 dark:border-gray-700 sticky top-32">
                  <h3 className="text-xl font-semibold mb-4">Our AI Infrastructure Services</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>GPU Clusters</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>TPU Deployment</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>High-Performance Computing</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>AI-optimized Storage</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>MLOps Platforms</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Distributed Training</span>
                    </li>
                  </ul>

                  <div className="mt-8">
                    <Link to="/contact">
                      <Button className="w-full">
                        Contact Us
                        <ArrowUpRight className="ml-1 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 p-8 rounded-2xl mb-16">
              <h2 className="text-2xl font-semibold mb-4">Ready to build robust AI infrastructure?</h2>
              <p className="text-muted-foreground mb-6">
                Let's discuss how our AI infrastructure solutions can help you accelerate AI development and deployment.
              </p>
              <Link to="/contact">
                <Button>
                  Get Started
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </PageTransition>
  );
};

export default AIInfrastructurePage;
